import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Paper,
  Fade,
  Grow
} from '@mui/material';
import {
  Close,
  PersonAdd,
  Login,
  Star,
  Security,
  Favorite,
  History,
  Chat,
  CheckCircle,
  ArrowForward
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const SignUpPromptModal = ({ open, onClose, featureName = 'this feature' }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignUp = () => {
    onClose();
    navigate('/signup', { state: { returnUrl: location.pathname } });
  };

  const handleLogin = () => {
    onClose();
    navigate('/login', { state: { returnUrl: location.pathname } });
  };

  const benefits = [
    { icon: <Star sx={{ color: '#1976d2' }} />, text: 'Book services and make purchases' },
    { icon: <Chat sx={{ color: '#1976d2' }} />, text: 'Chat with service providers' },
    { icon: <Favorite sx={{ color: '#1976d2' }} />, text: 'Save your favorite services' },
    { icon: <History sx={{ color: '#1976d2' }} />, text: 'Access your booking history' },
    { icon: <Security sx={{ color: '#1976d2' }} />, text: 'Secure payment processing' }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Grow}
      transitionDuration={300}
      PaperProps={{
        sx: {
          borderRadius: '20px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(25, 118, 210, 0.1)',
          boxShadow: '0 20px 40px rgba(25, 118, 210, 0.15)',
          overflow: 'hidden',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #90caf9 100%)'
          }
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'linear-gradient(135deg, #f8faff 0%, #ffffff 100%)',
        borderBottom: '1px solid rgba(25, 118, 210, 0.1)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
          }}>
            <PersonAdd sx={{ color: 'white', fontSize: 20 }} />
          </Box>
          <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            color: '#1976d2',
            fontSize: '1.25rem'
          }}>
            Join Our Community
          </Typography>
        </Box>
        <IconButton 
          onClick={onClose} 
          size="small"
          sx={{
            color: '#666',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              color: '#1976d2'
            }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Fade in timeout={500}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              boxShadow: '0 8px 24px rgba(25, 118, 210, 0.3)',
              animation: 'pulse 2s infinite'
            }}>
              <PersonAdd sx={{ fontSize: 40, color: 'white' }} />
            </Box>
          </Fade>
          
          <Typography variant="h5" sx={{ 
            fontWeight: 700, 
            mb: 2, 
            color: '#1976d2',
            fontSize: '1.5rem'
          }}>
            Create Your Account
          </Typography>
          
          <Typography variant="body1" sx={{ 
            color: '#666', 
            mb: 3, 
            lineHeight: 1.7,
            fontSize: '1rem',
            maxWidth: '400px',
            margin: '0 auto'
          }}>
            To access <strong>{featureName}</strong>, you need to create an account. 
            Join thousands of satisfied customers and unlock exclusive features.
          </Typography>
        </Box>
        
        <Paper elevation={0} sx={{ 
          background: 'linear-gradient(135deg, #f8faff 0%, #ffffff 100%)', 
          p: 3, 
          borderRadius: '16px', 
          mb: 3,
          border: '1px solid rgba(25, 118, 210, 0.1)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)'
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CheckCircle sx={{ color: '#1976d2', mr: 1, fontSize: 20 }} />
            <Typography variant="h6" sx={{ 
              color: '#1976d2', 
              fontWeight: 600,
              fontSize: '1.1rem'
            }}>
              Benefits of Creating an Account
            </Typography>
          </Box>
          
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            {benefits.map((benefit, index) => (
              <Fade in timeout={600 + index * 100} key={index}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  p: 1.5,
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.05)',
                    transform: 'translateX(4px)'
                  }
                }}>
                  <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {benefit.icon}
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: '#555', 
                    fontWeight: 500,
                    fontSize: '0.95rem'
                  }}>
                    {benefit.text}
                  </Typography>
                </Box>
              </Fade>
            ))}
          </Box>
        </Paper>
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 3, 
        pt: 0, 
        gap: 2,
        background: 'linear-gradient(135deg, #f8faff 0%, #ffffff 100%)',
        borderTop: '1px solid rgba(25, 118, 210, 0.1)'
      }}>
        <Button
          variant="outlined"
          onClick={handleLogin}
          startIcon={<Login />}
          endIcon={<ArrowForward />}
          sx={{
            borderColor: '#1976d2',
            color: '#1976d2',
            borderRadius: '12px',
            px: 3,
            py: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '0.95rem',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)'
            }
          }}
        >
          Already have an account?
        </Button>
        <Button
          variant="contained"
          onClick={handleSignUp}
          startIcon={<PersonAdd />}
          endIcon={<ArrowForward />}
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            borderRadius: '12px',
            px: 3,
            py: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '0.95rem',
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 20px rgba(25, 118, 210, 0.4)'
            }
          }}
        >
          Create Account
        </Button>
      </DialogActions>

      <style jsx>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
            box-shadow: 0 8px 24px rgba(25, 118, 210, 0.3);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 12px 32px rgba(25, 118, 210, 0.4);
          }
          100% {
            transform: scale(1);
            box-shadow: 0 8px 24px rgba(25, 118, 210, 0.3);
          }
        }
      `}</style>
    </Dialog>
  );
};

export default SignUpPromptModal;
